/** @file TileToolColumn: tools, toggles, zoom, local undo / redo, Save, and the colour section. */
import type { Rgba } from '@zilo/pose3d-image-io/types/image';
import type { ReactElement } from 'react';
import { useStore } from 'zustand';
import { Button } from '../../ui-kit/Button';
import { TileColorPanel } from './TileColorPanel';
import { tileDrafts } from './tileDrafts';
import { tilePaintUi } from './tilePaintUi';
import type { TileToggle, TileTool } from './tilePaintUi';

/**
 * The draft key, the zoom shown, whether the draft is dirty and a save runs, the palette, and the screen's zoom and
 * save operations (`zoom(0)` = fit).
 */
export type TileToolColumnProps = {
  draftKey: string; zoom: number; dirty: boolean; saving: boolean; palette: readonly Rgba[];
  onZoom: (dir: -1 | 0 | 1) => void; onSave: () => void;
};

const TOOLS: { id: TileTool; label: string; key: string }[] = [
  { id: 'pencil', label: 'Pencil', key: 'B' }, { id: 'eraser', label: 'Eraser', key: 'E' }, { id: 'bucket', label: 'Bucket', key: 'G' },
  { id: 'eyedropper', label: 'Pick', key: 'I' }, { id: 'line', label: 'Line', key: 'L' },
];
const TOGGLES: { id: TileToggle; label: string; key: string }[] = [
  { id: 'mirror', label: 'Mirror X', key: 'M' }, { id: 'onion', label: 'Onion', key: 'O' }, { id: 'grid', label: 'Grid', key: 'Shift+G' },
];

/** A labelled toggle button (`aria-pressed`) with its key in the tooltip. */
function Toggle({ label, keyName, on, onClick }: { label: string; keyName: string; on: boolean; onClick: () => void }): ReactElement {
  return <button type="button" className="p3-btn p3-tile-tool" aria-pressed={on} title={`${label} (${keyName})`} onClick={onClick}>{label}</button>;
}

/** The left column of the Tile screen. */
export function TileToolColumn({ draftKey, zoom, dirty, saving, palette, onZoom, onSave }: TileToolColumnProps): ReactElement {
  const ui = useStore(tilePaintUi);
  const canUndo = useStore(tileDrafts, (s) => (s.drafts[draftKey]?.undo.length ?? 0) > 0);
  const canRedo = useStore(tileDrafts, (s) => (s.drafts[draftKey]?.redo.length ?? 0) > 0);
  const drafts = tileDrafts.getState();
  return (
    <aside className="p3-tile-tools" aria-label="Tile tools">
      <div className="p3-tile-group" role="group" aria-label="Tools">
        {TOOLS.map((t) => <Toggle key={t.id} label={t.label} keyName={t.key} on={ui.tool === t.id} onClick={() => { ui.setTool(t.id); }} />)}
      </div>
      <div className="p3-tile-group" role="group" aria-label="Toggles">
        {TOGGLES.map((t) => <Toggle key={t.id} label={t.label} keyName={t.key} on={ui[t.id]} onClick={() => { ui.toggle(t.id); }} />)}
      </div>
      <div className="p3-row" style={{ gap: 4 }}>
        <Button title="Zoom out (-)" onClick={() => { onZoom(-1); }}>-</Button>
        <Button title="Zoom to fit (0)" onClick={() => { onZoom(0); }} className="p3-mono">{zoom}x</Button>
        <Button title="Zoom in (=)" onClick={() => { onZoom(1); }}>+</Button>
      </div>
      <div className="p3-row" style={{ gap: 4 }}>
        <Button title="Undo a stroke (Ctrl+Z)" disabled={!canUndo} onClick={() => { drafts.undo(draftKey); }}>Undo</Button>
        <Button title="Redo a stroke (Ctrl+Shift+Z)" disabled={!canRedo} onClick={() => { drafts.redo(draftKey); }}>Redo</Button>
      </div>
      <Button variant="primary" disabled={!dirty || saving} title="Upload and fill the slot (Ctrl+S)" onClick={onSave}>
        {saving ? 'Saving…' : 'Save'}
      </Button>
      <TileColorPanel palette={palette} />
    </aside>
  );
}
